import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can now sign in.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-8" data-testid="reset-back-link"><ArrowLeft className="h-4 w-4" /> Back to login</Link>
        {!token ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
            <h1 className="text-2xl font-extrabold text-[#0F2744]">Invalid reset link</h1>
            <p className="mt-3 text-slate-500">This link is missing its token. Please request a new one.</p>
            <Button asChild className="mt-6 bg-[#0F2744] hover:bg-[#0B192C]"><Link to="/forgot-password" data-testid="reset-request-new">Request a new link</Link></Button>
          </div>
        ) : done ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="reset-done">
            <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="h-7 w-7 text-green-600" /></div>
            <h1 className="text-2xl font-extrabold text-[#0F2744]">Password updated</h1>
            <p className="mt-3 text-slate-500">Redirecting you to the login page…</p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold text-[#0F2744]">Set a new password</h1>
            <p className="mt-2 text-slate-500">Choose a strong password of at least 8 characters.</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
              <div>
                <Label className="mb-1.5 block">New password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password" />
              </div>
              <div>
                <Label className="mb-1.5 block">Confirm new password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full bg-[#0F2744] hover:bg-[#0B192C] h-12" data-testid="reset-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
